import {CellValue, DurationUnit, ReportDataResult, ReportRow, RowType} from './reportRowModel';
import {ReportDefinitionV1} from '../contracts/report-definition.types';
import {
	fieldByKey,
	ReportTaxonomy,
	ReportTaxonomyField,
	ROW_REASON_NOT_APPLICABLE_KEYS,
} from '../contracts/report-taxonomy.types';
import {flattenFieldPaths, grainRootOf} from './definitionFields';
import {richTextToPlain} from './richTextToPlain';
import {budgetTypeLabelFor} from '../projectBudgetTypeLabels';
import {stageLabelFor} from '../projectStageLabels';
import {FormatLabel, ragStateFor, ragStateLabel} from '../projectStatusLabels';
import {profilePicSrc, projectUrl} from '../../../directApi';

/**
 * Maps the LOCKED `POST /report/data/fetch` (hierarchical) response — a bare array of wrapped,
 * self-describing row nodes — into the `ReportRow` tree the table renders, against the shipped
 * hierarchical report-data fetch contract.
 *
 * A wrapped node is `{ name, value }`. A ROW node's `name` is its type (`task`/`subtask`/
 * `deliverable`) and its `value` is an array of FIELD nodes. Field nodes are either scalar
 * (`{name:'companyTaskId', value:3}`) or nested (`{name:'parent', value:[{name:'companyTaskId',
 * value:null}]}` / `{name:'children', value:[<row nodes>]}`).
 *
 * A few field nodes are COMPOUND: their sub-fields collapse into a single typed `CellValue` under a
 * display-column key rather than one cell per leaf. These are shaped by the helpers below against
 * the emitted wire shapes verified in the report-service converters/resolvers:
 * - `project`   → `<grain>.project.info`      `{kind:'project'}`
 * - an array of field-lists → `{kind:'persons'}` or `{kind:'tag'}`, keyed by the column(s) the
 *   definition displays it under (a task's `assignees`, a project's `owner` or `labels`)
 * - `work`      → `task.work.estimate` + `task.work.remaining`  `{kind:'duration'}`
 * - `reported`  → `task.reported.time`         `{kind:'duration'}`
 *
 * Cells are keyed under the REPORT's field-tree root rather than the row node's own name: a fetch
 * that nests its rows renames them, so the row cannot say which grain it belongs to. What a scalar
 * leaf MEANS - a duration, a date, a percentage - is likewise declared by the catalog rather than
 * guessed from the value, with a shape-based fallback for every leaf the catalog does not describe.
 */

export interface FieldNode {
	name: string;
	value: FieldNodeValue;
}
// `assignees` is an array of person field-lists (array-of-arrays), so the union admits FieldNode[][].
export type FieldNodeValue = string | number | boolean | null | FieldNode[] | FieldNode[][];

/** Row-node names that map straight to `ReportRow.type`. Exported so anything that has to walk the
 * same wire tree (the fixture conformance check) gates on this exact list rather than its own copy,
 * which would silently disagree the day another row type is recognised here. */
export const ROW_TYPES: readonly RowType[] = ['task', 'subtask', 'deliverable', 'projectStatus', 'timeRegAuditEvent'];

/** The field-tree root a row's cells are keyed under when the caller names none. Every definition
 * predating the grain concept is a task report, and so is every existing caller. */
const DEFAULT_ROOT = 'task';

/**
 * What the mapper needs to know about the report the rows belong to.
 *
 * `root` is the definition's own field-tree root, NOT the row node's name. The two agree on a fetch
 * that returns rows verbatim, but the task hierarchy assembler renames a row it is asked to nest,
 * so the row's name cannot be trusted to identify its grain and the definition can.
 *
 * `columnPaths` are the definition's leaf field paths - what the table asks for. A compound node
 * collapses into ONE cell, and only the definition says which key that cell is displayed under
 * (`owner` is shown as `owner.name`, `labels` as `labels`), so the mapper reads it from here rather
 * than guessing a suffix.
 *
 * `taxonomy` types the leaves: which are durations, dates or percentages is declared per field and
 * cannot be inferred from a value that arrives as a bare number. It is optional because it resolves
 * asynchronously and the first fetch must not wait for it - without it, leaves fall back to the
 * shape-based inference below and the rows are re-mapped when it lands.
 */
export interface RowMappingContext {
	root: string;
	columnPaths?: readonly string[];
	taxonomy?: ReportTaxonomy | null;
	/** Resolves a display label to the reader's language. Optional because the mapper must stay a
	 * pure function usable outside React - a fixture, a test, or a fetch that runs before an intl
	 * context exists still maps, in English, rather than emitting a raw message id. */
	formatLabel?: FormatLabel;
}

const DEFAULT_CONTEXT: RowMappingContext = {root: DEFAULT_ROOT};

/**
 * The mapping context for a report: its own field-tree root, the leaf paths it displays, and the
 * catalog that types them.
 *
 * The root comes from the definition rather than from the rows, and a definition with no fields at
 * all keeps the task root - the meaning every definition predating the grain concept carries.
 */
export function rowMappingContextFor(
	definition: Pick<ReportDefinitionV1, 'fields'>,
	taxonomy?: ReportTaxonomy | null,
	formatLabel?: FormatLabel
): RowMappingContext {
	const fields = definition.fields ?? [];
	return {
		root: grainRootOf(definition),
		columnPaths: flattenFieldPaths(fields),
		taxonomy,
		formatLabel,
	};
}

/** Field-node names that are purely structural — never emitted as cells. `name` and `parent`
 * ARE cells (`task.name`, `task.parent.companyTaskId`), so they are NOT in this set. */
const STRUCTURAL_FIELDS = new Set(['children', 'contextOnly']);

function isFieldNodeArray(v: FieldNodeValue): v is FieldNode[] {
	return Array.isArray(v) && (v.length === 0 || !Array.isArray(v[0]));
}

/** An array of field-lists: the wire shape for a repeated sub-entity (a task's assignees, a
 * project's owner or labels). Distinguished from a nested node purely by its second dimension. */
function isFieldNodeArrayArray(v: FieldNodeValue): v is FieldNode[][] {
	return Array.isArray(v) && v.length > 0 && Array.isArray(v[0]);
}

/** Find a field node by short name inside a field-node array; return its value (or null). */
export function field(values: FieldNode[], name: string): FieldNodeValue | null {
	const node = values.find(n => n.name === name);
	return node ? node.value : null;
}

/**
 * Shape a duration leaf into `{kind:'duration', minutes, unit}`. The `CellValue` duration model (and
 * `formatDuration` in `ReportTable/durationFormat`) always divides `minutes` by 60, so the stored `minutes` is
 * `unit-value × 60` for both units. `task.reported.time` arrives in raw minutes
 * (`sourceIsMinutes = true`). When the unit is unknown the value cannot be formatted as a
 * duration, so it degrades to a plain number.
 *
 * The unit-varying columns are the ones that need `unitScaleOf` rather than a fixed answer here:
 * their scale is not the same on both units. See its comment.
 */
function shapeDuration(value: FieldNodeValue, unit: DurationUnit | undefined, sourceIsMinutes: boolean): CellValue {
	if (typeof value !== 'number') {
		return {kind: 'na'};
	}
	if (!unit) {
		return {kind: 'number', value};
	}
	return {kind: 'duration', minutes: sourceIsMinutes ? value : value * 60, unit};
}

/**
 * Whether a unit-varying value arrives as raw minutes, for `shapeDuration`'s `sourceIsMinutes`.
 *
 * The wire is not one scale, and that is the whole reason this exists. service-company returns each
 * of these straight off the task's own stored column, so an HOURS project sends MINUTES
 * (`forecast_minutes`) while a POINTS project sends the point count itself. Measured against a live
 * stack: a task stored as 90 minutes on an HOURS project arrives as `90`, not `1.5`, and a 20-point
 * task on a POINTS project arrives as `20`.
 *
 * Treating both as unit-values - the assumption this file carried until it was checked - renders
 * every hours estimate, remaining and variance 60x too large: a 10-hour task read `600h`.
 */
function unitVaryingSourceIsMinutes(unit: DurationUnit | undefined): boolean {
	return unit === 'hours';
}

/** The duration unit a declared field is measured in. `hours` is the fallback rather than a failure:
 * a duration the catalog left unitless is still time, and rendering it as a plain number instead
 * would be a worse answer than assuming the only unit any grain publishes today. */
function durationUnitOf(declared: ReportTaxonomyField): DurationUnit {
	return declared.unit === 'points' ? 'points' : 'hours';
}

/**
 * Shape a SCALAR leaf value into a `CellValue` for a given field path. Compound nodes are
 * handled upstream in `flattenToPathKeys` and never reach here.
 *
 * Note `task.status.name` is a scalar here: it carries the workflow-status NAME (a string), which
 * renders as text. The RAG traffic-light (`{kind:'status'}`) is a SEPARATE, client-DERIVED value
 * from `task.derived.overEstimate`/`overdue`; its 2-signals → 3-states mapping is an open product
 * decision, so it is deliberately NOT synthesised here.
 */
function shapeCell(path: string, value: FieldNodeValue, declared?: ReportTaxonomyField): CellValue {
	if (value === null || value === undefined) {
		return {kind: 'na'};
	}
	// What a leaf MEANS is declared, not inferrable: a duration, a percentage and a plain count all
	// arrive as a bare number, and a date arrives as a string. The declaration is preferred wherever
	// the taxonomy has resolved; the shape-based rules below still answer for every leaf it does not
	// describe, and for every fetch that landed before it resolved.
	if (declared) {
		if (declared.dataType === 'duration' && typeof value === 'number') {
			// Every duration LEAF on the wire is minutes. The task grain's estimate/remaining are the
			// exception that proves it: they carry a unit-value rather than minutes, and they are
			// collapsed by the compound handling below rather than reaching here.
			return shapeDuration(value, durationUnitOf(declared), true);
		}
		if (declared.dataType === 'date' && typeof value === 'string') {
			return {kind: 'date', value};
		}
		if (declared.dataType === 'number' && declared.unit === 'percent' && typeof value === 'number') {
			return {kind: 'percent', value};
		}
		if (declared.dataType === 'string' && typeof value === 'string') {
			// Declared text wins over the path heuristic below. A company custom field's LABEL is a
			// path segment (`projectStatus.customFields.<Label>`), so a field a company
			// happens to have called "dates" would otherwise be read as a date: it would render as
			// an invalid date, and grouping by it would put every row in the "No dates" bucket,
			// because `labelsForToken` contributes no label for a date cell. No declared-string
			// field sits under a `dates` node in either published catalog, so this cannot take a
			// real date away from the rule below.
			return {kind: 'text', value};
		}
	}
	// A catalog boolean, declared or not — there is no conflicting declared type a boolean could
	// collide with. The null case is handled above and never reaches here as `false`.
	if (typeof value === 'boolean') {
		return {kind: 'boolean', value};
	}
	if (path.split('.').includes('dates') && typeof value === 'string') {
		return {kind: 'date', value};
	}
	if (typeof value === 'number') {
		return {kind: 'number', value};
	}
	if (typeof value === 'string') {
		return {kind: 'text', value};
	}
	return {kind: 'na'};
}

/** The one column key whose scalar is a traffic light rather than its own text. Spelled in full
 * because it is grain-specific: `task.status.name` is a workflow-status NAME and stays text, and
 * shaping any other `rag`-shaped leaf this way would be a guess. */
const PROJECT_STATUS_RAG_KEY = 'projectStatus.rag';

/** The project grain's lifecycle stage, whose wire tokens (`HALTED`) are not what a reader is
 * shown (`On Hold`). Spelled in full for the same reason as the RAG key: only this grain publishes
 * a stage, and relabelling any leaf named `stage` would be a guess. */
const PROJECT_STATUS_STAGE_KEY = 'projectStatus.stage';

/**
 * A project's RAG status as the three-state traffic light plus the fourth, statusless state.
 *
 * The wire value (`Green`/`Yellow`/`Red`/`No or other status`) is NOT what a reader sees, so the
 * label is resolved here rather than passed through: a cell showing `Green` would be both the wrong
 * vocabulary and a different one from the group heading beside it. Anything unrecognised - a value
 * a later report-service publishes, a null, an absent status - resolves to the em-dash state, which
 * is the point: a project nobody has set a status on must not read as healthy.
 */
function shapeRag(value: FieldNodeValue, formatLabel?: FormatLabel): CellValue {
	const rag = ragStateFor(value);
	return {kind: 'status', rag, label: ragStateLabel(rag, formatLabel)};
}

/** A project's lifecycle stage as its display label. Stays an ordinary text cell - the stage has no
 * traffic-light treatment and must not read like one - but carries the reader's wording, so the
 * column, the group heading and the filter picker all say the same thing. A project with no stage
 * at all has no label to show, and reads as `N/A` like every other absent scalar rather than as an
 * empty cell that says nothing. */
function shapeStage(value: FieldNodeValue, formatLabel?: FormatLabel): CellValue {
	const label = stageLabelFor(value, formatLabel);
	return label === '' ? {kind: 'na'} : {kind: 'text', value: label};
}

/** A description is stored as draft-js JSON, legacy HTML or plain text; the cell shows its text. */
const TASK_DESCRIPTION_KEY = 'task.description';

function shapeDescription(value: FieldNodeValue): CellValue {
	if (typeof value !== 'string') {
		return {kind: 'na'};
	}
	const text = richTextToPlain(value);
	return text === '' ? {kind: 'na'} : {kind: 'text', value: text};
}

/** The project grain's budget type, whose wire tokens (`TIME_AND_MATERIALS`, `FIXED_PRICE_V2`) are not
 * what a reader is shown. Spelled in full for the same reason as the stage key. */
const PROJECT_STATUS_BUDGET_TYPE_KEY = 'projectStatus.budgetType';

/** A project's budget type as its row label - the column, the group heading and the truncation
 * tooltip all read the cell. Both fixed-price enums read `Fixed Price`, so grouping, which buckets on
 * the cell text, puts them in one group, and the totals rule, which compares the same text, sums
 * their hours instead of withholding them as mixed budget types: they are one kind of budget,
 * differing only in how progress is derived. A project with no budget type reads `N/A`, as a project
 * with no stage does. */
function shapeBudgetType(value: FieldNodeValue, formatLabel?: FormatLabel): CellValue {
	const label = budgetTypeLabelFor(value, formatLabel);
	return label === '' ? {kind: 'na'} : {kind: 'text', value: label};
}

/** Scalar leaves whose column key alone decides their shape, ahead of the type-driven rules in
 * `shapeCell`. Keyed by the FULL column key rather than the leaf name so a same-named leaf on
 * another grain is untouched. A `Map` so the lookup cannot answer off the prototype chain and so
 * the shaper is found once rather than twice per leaf. */
const GRAIN_SHAPERS = new Map<string, (value: FieldNodeValue, formatLabel?: FormatLabel) => CellValue>([
	[PROJECT_STATUS_RAG_KEY, shapeRag],
	[PROJECT_STATUS_STAGE_KEY, shapeStage],
	[PROJECT_STATUS_BUDGET_TYPE_KEY, shapeBudgetType],
	[TASK_DESCRIPTION_KEY, shapeDescription],
]);

/** The one unit-varying scalar leaf named outright rather than read off the catalog. The catalog
 * resolves asynchronously and the first fetch must not wait for it, so without this literal the
 * column renders unitless until the catalog lands. Everything else about it is answered by
 * `isUnitVaryingScalar` below. */
const TASK_VARIANCE_KEY = 'task.derived.variance';

/**
 * A scalar leaf whose value is in the ROW's estimation unit rather than in minutes or as a plain
 * count, and which therefore takes the row unit the way `task.work.estimate` does.
 *
 * The catalog is what says so: `unitVaries` marks a field whose unit is per-row rather than fixed
 * for the grain. Driving this off the declaration rather than off a list of keys is what stops the
 * next unit-varying column repeating the defect this exists to fix - shaped as a bare number it
 * renders unitless (a points value of 3 reading identically to an hours one) and, because only
 * duration cells are totalled, it carries neither a group total nor the withheld explanation a
 * mixed-unit group owes the reader.
 *
 * `dataType` is checked too, because the two number-shaped cases are opposites on the wire: a
 * declared `duration` leaf is MINUTES (shaped by `shapeCell`), while a `number` leaf declared
 * unit-varying carries the unit-value `shapeDuration` scales by 60.
 *
 * Not a `GRAIN_SHAPERS` entry: those shapers see only the leaf value, and this one needs the row's
 * estimation unit, which is why it is handled in `flattenToPathKeys` beside the compound `work`
 * node rather than in `shapeCell`.
 */
function isUnitVaryingScalar(key: string, declared?: ReportTaxonomyField): boolean {
	return key === TASK_VARIANCE_KEY || (declared?.unitVaries === true && declared.dataType === 'number');
}

/**
 * `task.project.info` pill. `id` (the pill's `WorkSignifier` workId) mirrors service-company's
 * `ProjectIndicator`: `customProjectId` when present, else `P${companyProjectId}`. `color` =
 * `projectColor`, `name` = `name`.
 *
 * `projectKey`: DECIDED (standup) — mirror what the app already does: the pill id is `customProjectId`
 * (else `P${companyProjectId}`), and there is no separate project "key". There is no distinct key
 * field in the data (ProjectDTO carries only `companyProjectId` / `customProjectId` / `name` /
 * `projectColor`); the design's `ATL`-style key was fixture-invented, not a real element. `projectKey`
 * therefore reuses the id; revisit only if a real key source is added later. Project sub-fields beyond
 * id are only emitted when `fullAccessToProject === true`; a context/no-access project (name/color
 * absent) → `na`.
 */
function shapeProject(value: FieldNodeValue): CellValue {
	if (!isFieldNodeArray(value)) {
		return {kind: 'na'};
	}
	const name = field(value, 'name');
	const color = field(value, 'projectColor');
	if (typeof name !== 'string' || typeof color !== 'string') {
		return {kind: 'na'};
	}
	const custom = field(value, 'customProjectId');
	const companyProjectId = field(value, 'companyProjectId');
	const customId = typeof custom === 'string' && custom !== '' ? custom : null;
	// Zero is not a project. Treating it as one builds `/project/P-0/scoping`, a link to nothing, and
	// shows a `P0` pill beside it; leaving it out renders the cell unlinked, which is what a row with
	// no usable project id is supposed to do.
	const hasCompanyId = typeof companyProjectId === 'number' && companyProjectId > 0;
	const id = customId ?? (hasCompanyId ? `P${companyProjectId}` : '');
	const projectKey = typeof custom === 'string' ? custom : '';
	// The pill id is a DISPLAY string (`P8888`); the project path is a different shape
	// (`/project/P-8888`, or the custom id when the project has one), so the link is built from
	// the raw ids via the app's own helper rather than from `id`. `/scoping` is a deliberate
	// destination, not a default: the app links to a project by several different pages depending
	// on where the user came from, and a report row is a task, so scoping — where that task sits in
	// the project's breakdown — is the page this link is meant to land on. Null when neither id is
	// present — there is no page to link to, and the cell renders unlinked.
	const href = customId !== null || hasCompanyId ? `${projectUrl(companyProjectId, customId)}/scoping` : null;
	return {kind: 'project', id, name, projectKey, color, href};
}

/**
 * `task.assignees.firstName` persons cell. The wire shape is an array of person field-lists (each an
 * array of `{id, firstName, lastName, profilePictureId, ...}`), verified in
 * `ServiceCompanyEntityResolver.resolveSubEntity` (it clears each `{id}` stub and refills it with the
 * resolved person's fields). `avatarUrl` is built from `profilePictureId` via
 * `directApi.profilePicSrc`, omitted when the person has no picture id.
 *
 * Square, NOT the `profilePicRectSrc` rect variant some other tables use. `Person` renders the
 * photo as an img with a fixed height and auto width into a square 24px slot, so a rect crop comes
 * out off-aspect beside the square initials hexagons - visible the moment a person actually has a
 * picture. The Project Portfolio Standard Report, which this column mirrors, uses the square helper
 * too, and it is the safer of the two: profilePicSrc returns null for a falsy id so the component
 * falls back to initials, where the rect variant builds a URL ending in undefined.
 */
/** `useFullName` is decided by the catalog key the cell is displayed under, not by what the wire
 * happens to carry. `task.assignees.firstName` is labelled "Assignee first name", so that column
 * shows the first name even on the rare row that also carries a surname; `projectStatus.owner.name`
 * is labelled "Owner / PM" and shows the full name, matching the Project Portfolio Standard Report,
 * which passes `fullName` to both the avatar and its tooltip. */
function shapePersons(value: FieldNodeValue, useFullName: boolean): CellValue {
	if (!Array.isArray(value)) {
		return {kind: 'persons', persons: []};
	}
	const persons = (value as unknown[])
		.filter((entry): entry is FieldNode[] => Array.isArray(entry))
		.map(personFields => {
			const id = field(personFields, 'id');
			const first = field(personFields, 'firstName');
			const last = field(personFields, 'lastName');
			const pictureId = field(personFields, 'profilePictureId');
			const avatarUrl =
				typeof pictureId === 'number' || typeof pictureId === 'string' ? profilePicSrc(pictureId) : undefined;
			// The join also degrades safely when a surname is simply absent, which is the normal
			// case for a task assignee: the wire carries no `lastName` leaf at all there.
			const name = [typeof first === 'string' ? first : '', useFullName && typeof last === 'string' ? last : '']
				.filter(part => part !== '')
				.join(' ');
			return {
				id: id === null || id === undefined ? '' : String(id),
				name,
				...(avatarUrl ? {avatarUrl} : {}),
			};
		})
		.filter(person => person.id !== '' || person.name !== '');
	return {kind: 'persons', persons};
}

/**
 * A `tag` cell from an array of label field-lists (`[{id, name, color}, ...]`). Entries with no name
 * are dropped rather than rendered as an empty chip; a colour the wire omits falls back to empty,
 * which the renderer treats as an uncoloured tag.
 */
function shapeTags(value: FieldNodeValue): CellValue {
	if (!Array.isArray(value)) {
		return {kind: 'tag', tags: []};
	}
	const tags = (value as unknown[])
		.filter((entry): entry is FieldNode[] => Array.isArray(entry))
		.map(tagFields => {
			const name = field(tagFields, 'name');
			const color = field(tagFields, 'color');
			return {
				name: typeof name === 'string' ? name : '',
				color: typeof color === 'string' ? color : '',
			};
		})
		.filter(tag => tag.name !== '');
	return {kind: 'tag', tags};
}

/**
 * Which cell an array of field-lists collapses into, decided by what its entries carry rather than
 * by the field's name: an entry with a person's name is a person, an entry with a colour is a tag.
 * Naming the fields instead would be the same fixed list of task field names this mapper is being
 * generalised out of - a grain's own `owner` or `labels` would match none of them.
 */
function shapeEntryArray(entries: FieldNode[][], useFullName: boolean): CellValue {
	const first = entries[0] ?? [];
	const looksLikePerson =
		first.some(node => node.name === 'firstName') ||
		first.some(node => node.name === 'lastName') ||
		first.some(node => node.name === 'profilePictureId');
	return looksLikePerson ? shapePersons(entries, useFullName) : shapeTags(entries);
}

/** Estimation unit for the row, read from the sibling `project` node's `estimationUnit`
 * (`HOURS`/`POINTS`). Undefined when the project node or the field is absent — the fetch must
 * request `project.estimationUnit` for durations to render with a unit. */
function rowEstimationUnit(rowValues: FieldNode[]): DurationUnit | undefined {
	const project = rowValues.find(n => n.name === 'project');
	if (!project || !isFieldNodeArray(project.value)) {
		return undefined;
	}
	const unit = field(project.value, 'estimationUnit');
	if (unit === 'HOURS') return 'hours';
	if (unit === 'POINTS') return 'points';
	return undefined;
}

/** The project node's own scalar catalog keys — the leaves that are columns in their own right
 * rather than parts of the pill. `info` is not here (it is the whole node, shaped separately) and
 * neither are the four identity leaves behind it, which have no catalog keys of their own. */
const PROJECT_SCALAR_CATALOG_LEAVES = ['estimationUnit', 'minutesPerEstimationPoint'] as const;

/** The leaf carrying the reason. The six columns it explains are `ROW_REASON_NOT_APPLICABLE_KEYS`,
 * shared with the conditions editor - which withholds the exclude operators from exactly the fields
 * a reason can empty - so the two cannot name different sets. */
const RETAINER_REASON_KEY = 'projectStatus.retainer.notApplicable';

/**
 * Moves the row's retainer reason onto the retainer cells it explains, then removes it.
 *
 * A retainer column can be empty for four different reasons, and an empty cell tells a reader
 * nothing. report-service sends the reason as a sibling leaf of the six values (forced onto the
 * response whenever a retainer column is present, since it is not a column anyone can select), and
 * the reason belongs on the CELLS rather than beside them: a reader looking at an empty "Retainer
 * target" wants the answer in that cell, not in a seventh column.
 *
 * Attached only where the value is actually absent. Which columns a reason empties differs per
 * reason - a period that is not Fixed-Hours keeps its dates and its consumed figure - so a row can
 * legitimately show a figure in one retainer column and an explanation in the next.
 *
 * The reason leaf itself is deleted afterwards, because it is not a column: left in `cells` it would
 * be renderable by any layout that happened to name its path, showing a reader a raw token.
 */
function applyRetainerReason(cells: Record<string, CellValue>): void {
	const carrier = cells[RETAINER_REASON_KEY];
	if (carrier === undefined) {
		// Every row of every other grain, and every project-status row with no retainer column,
		// lands here: nothing to move and nothing to remove.
		return;
	}
	delete cells[RETAINER_REASON_KEY];
	if (carrier.kind !== 'text' || carrier.value === '') {
		return;
	}
	for (const key of ROW_REASON_NOT_APPLICABLE_KEYS) {
		const cell = cells[key];
		if (cell && cell.kind === 'na') {
			cells[key] = {kind: 'na', reason: carrier.value};
		}
	}
}

/**
 * Flatten a row node's field-node array into `Record<field path, CellValue>`. Scalars recurse into
 * dotted paths (`parent` → `task.parent.companyTaskId`); compound row-level nodes
 * (`project`/`role`/`assignees`/`work`/`reported`) collapse to their display-column key(s);
 * structural fields (`children`/`contextOnly`) are skipped. `project` is the one node that
 * collapses AND descends: the pill takes the node as a whole, and `project.client.name` is a
 * catalog key of its own that would otherwise be swallowed with it.
 */
export function flattenToPathKeys(
	values: FieldNode[],
	context: RowMappingContext = DEFAULT_CONTEXT
): Record<string, CellValue> {
	const cells: Record<string, CellValue> = {};
	const unit = rowEstimationUnit(values);
	const {root, taxonomy, formatLabel} = context;
	const isTaskGrain = root === DEFAULT_ROOT;

	/** The column key(s) a compound node at `<root>.<name>` is displayed under: every leaf the
	 * definition selected beneath it, or the node's own path when it selected none (the value is
	 * still carried, so a column added later needs no refetch). */
	const displayKeysFor = (name: string): string[] => {
		const nodePath = `${root}.${name}`;
		const selected = (context.columnPaths ?? []).filter(path => path === nodePath || path.startsWith(`${nodePath}.`));
		return selected.length > 0 ? selected : [nodePath];
	};

	const walk = (nodes: FieldNode[], prefix: string): void => {
		for (const node of nodes) {
			if (STRUCTURAL_FIELDS.has(node.name)) {
				continue;
			}
			// Compound nodes only at the row level (prefix === '').
			if (prefix === '') {
				if (node.name === 'project') {
					// `<grain>.project.info` is the display key every grain that shows a project
					// column selects, so the pill is keyed off the report's own root.
					cells[`${root}.project.info`] = shapeProject(node.value);
					// The client hangs off the project, not the row: a task's client IS its
					// project's client, and `<grain>.project.client.name` is the catalog key the
					// Client column and the `client` grouping token both read. Collapsing the
					// project node to the pill alone would swallow it, so descend for this one
					// leaf. Absent whenever the viewer lacks full access to the project —
					// ProjectToNodeConverter withholds the whole client node then — which is a
					// missing cell, not an empty one.
					if (isFieldNodeArray(node.value)) {
						const client = node.value.find(child => child.name === 'client');
						if (client && isFieldNodeArray(client.value)) {
							const clientName = field(client.value, 'name');
							if (typeof clientName === 'string') {
								cells[`${root}.project.client.name`] = {kind: 'text', value: clientName};
							}
						}
						// The unit metadata is a pair of catalog keys too, so a definition may name
						// either as a column, and collapsing the node would swallow both the same
						// way it would have swallowed the client. They reach the mapper whether or
						// not the definition asked for them — report-service sends them with any
						// unit-bearing response so `rowEstimationUnit` can render durations (see
						// `buildFetchBody`) — but being read for that is not the same as being
						// renderable, and until they were emitted here a layout naming "Estimation
						// unit" produced the `N/A` sentinel on every row.
						for (const leaf of PROJECT_SCALAR_CATALOG_LEAVES) {
							const value = field(node.value, leaf);
							if (value !== undefined) {
								const path = `${root}.project.${leaf}`;
								cells[path] = shapeCell(path, value, taxonomy ? fieldByKey(taxonomy, path) : undefined);
							}
						}
					}
					continue;
				}
				if (isTaskGrain && node.name === 'role') {
					// Same display-key convention as the project pill, and the same reason it needs
					// a case here: the catalog publishes `task.role.info`, while the wire carries a
					// `role` node whose display value is its `name` leaf. Without this the Role
					// column — one of the default columns — reads an absent cell and renders the
					// `N/A` sentinel on every row, and grouping by role collapses the report into a
					// single "No role" bucket.
					const roleName = isFieldNodeArray(node.value) ? field(node.value, 'name') : null;
					cells['task.role.info'] = typeof roleName === 'string' ? {kind: 'text', value: roleName} : {kind: 'na'};
					continue;
				}
				if (isTaskGrain && node.name === 'assignees') {
					cells['task.assignees.firstName'] = shapePersons(node.value, false);
					continue;
				}
				if (isTaskGrain && node.name === 'work' && isFieldNodeArray(node.value)) {
					const sourceIsMinutes = unitVaryingSourceIsMinutes(unit);
					cells['task.work.estimate'] = shapeDuration(field(node.value, 'estimate'), unit, sourceIsMinutes);
					cells['task.work.remaining'] = shapeDuration(field(node.value, 'remaining'), unit, sourceIsMinutes);
					continue;
				}
				if (isTaskGrain && node.name === 'reported' && isFieldNodeArray(node.value)) {
					// Logged time is always tracked in time — never the project estimation unit
					// (a POINTS project still logs its work in time) — so it always renders as an
					// hours duration, independent of `unit`.
					cells['task.reported.time'] = shapeDuration(field(node.value, 'time'), 'hours', true);
					continue;
				}
				if (isFieldNodeArrayArray(node.value)) {
					// A repeated sub-entity: one cell listing every entry, under whichever leaf the
					// definition displays it as. Without this the node matches neither the scalar nor
					// the nested branch below and produces no cell at all.
					const keys = displayKeysFor(node.name);
					// A key that names `firstName` is asking for the first name only; anything else
					// (`owner.name`) wants the full one. Read off the key rather than the grain, so a
					// future grain gets the behaviour its own catalog advertises.
					const cell = shapeEntryArray(node.value, !keys.some(key => key.endsWith('.firstName')));
					for (const key of keys) {
						cells[key] = cell;
					}
					continue;
				}
			}
			const path = prefix ? `${prefix}.${node.name}` : node.name;
			if (isFieldNodeArray(node.value)) {
				walk(node.value, path);
			} else if (!Array.isArray(node.value)) {
				const key = `${root}.${path}`;
				const grainShaper = GRAIN_SHAPERS.get(key);
				if (grainShaper) {
					cells[key] = grainShaper(node.value, formatLabel);
					continue;
				}
				const declared = taxonomy ? fieldByKey(taxonomy, key) : undefined;
				if (isUnitVaryingScalar(key, declared)) {
					// `task.derived.variance` is estimate minus actual, expressed in the project's
					// own estimation unit rather than in minutes (report-service
					// `TaskDerivedFields.variance` divides by the per-task minutes ratio), which is
					// why the catalog declares it unit-varying and why the response forces the unit
					// alongside it. So it takes the row unit, and the same per-unit scale, as
					// `task.work.estimate` does: it is derived from that estimate and carries whatever
					// scale the estimate arrived in.
					cells[key] = shapeDuration(node.value, unit, unitVaryingSourceIsMinutes(unit));
					continue;
				}
				cells[key] = shapeCell(path, node.value, declared);
			}
		}
	};

	walk(values, '');
	applyRetainerReason(cells);
	return cells;
}

/** A row node's id. Tasks/subtasks are keyed by `companyTaskId`. Deliverable rows carry no
 * companyTaskId but a stable `id` field (verified: report-service `TaskHierarchyAssembler`
 * `newDeliverableRow` emits `{id, name}`, and a deliverable row is only promoted when that id is
 * non-null) — prefer it, type-prefixed to avoid colliding with a task's bare companyTaskId, so
 * React keys + TanStack expansion state survive a re-fetch that reorders rows. Only a node missing
 * both falls back to a synthetic parent + type + sibling-index id. */
function rowId(values: FieldNode[], type: RowType, parentId: string | null, index: number): string {
	const cid = field(values, 'companyTaskId');
	if (typeof cid === 'number' || typeof cid === 'string') {
		return String(cid);
	}
	const rid = field(values, 'id');
	if (typeof rid === 'number' || typeof rid === 'string') {
		return `${type}/${rid}`;
	}
	return `${parentId ?? 'root'}/${type}/${index}`;
}

/** The row's task id, when it has one. Only a numeric id can address the task modal, so a
 * string or absent value yields null and the row renders unlinked. */
function companyTaskIdOf(values: FieldNode[]): number | null {
	const cid = field(values, 'companyTaskId');
	return typeof cid === 'number' ? cid : null;
}

/** Real hierarchy is project → deliverable → task → subtask (~4 tiers). This ceiling sits far
 * above any legitimate depth and exists only to stop malformed or cyclic `children` in the
 * response from recursing without bound (stack overflow); rows deeper than this are dropped. */
const MAX_HIERARCHY_DEPTH = 20;

/** Map one wrapped row node (+ its descendants) into a `ReportRow`. `depth` is 1-based for the
 * row being mapped; recursion stops descending once it reaches `MAX_HIERARCHY_DEPTH`. */
export function toReportRow(
	node: FieldNode,
	parentId: string | null,
	index: number,
	depth = 1,
	context: RowMappingContext = DEFAULT_CONTEXT
): ReportRow {
	const values: FieldNode[] = isFieldNodeArray(node.value) ? node.value : [];
	const type = (ROW_TYPES.includes(node.name as RowType) ? node.name : 'task') as RowType;
	const id = rowId(values, type, parentId, index);
	// A row is task-backed only if it is a task tier AND actually carries a task's own id. The type
	// alone is not enough: an unrecognised row name falls back to `task` above, and a fetch that
	// nests its rows renames them on the way out, so a row of another grain can arrive claiming to be
	// a task. Every action that addresses a task hangs off this, so it asks for the id itself.
	const isTaskOrSubtask = (type === 'task' || type === 'subtask') && companyTaskIdOf(values) !== null;

	const childNodesValue = field(values, 'children');
	const childNodes: FieldNode[] = isFieldNodeArray(childNodesValue) ? childNodesValue : [];

	// A deliverable row has no backing task, so it can never be favourited even if a stray
	// `favoured` field were present in its data — only task/subtask rows carry a real value.
	const favorite = isTaskOrSubtask && field(values, 'favoured') === true;

	// The task's raw database id, distinct from `id` above (which is companyTaskId). Only task/subtask
	// rows have a backing task; a deliverable's own `id` field means something else entirely (see
	// `rowId`'s fallback) and must not be mistaken for a task id.
	const rawTaskId = isTaskOrSubtask ? field(values, 'id') : null;
	const taskId = typeof rawTaskId === 'number' || typeof rawTaskId === 'string' ? String(rawTaskId) : undefined;

	return {
		id,
		type,
		parentId,
		companyTaskId: companyTaskIdOf(values),
		// LOCKED: a back-filled context-ancestor carries `contextOnly: true`; matched rows omit it.
		matchedFilter: field(values, 'contextOnly') !== true,
		favorite,
		...(taskId !== undefined ? {taskId} : {}),
		cells: flattenToPathKeys(values, context),
		children:
			depth >= MAX_HIERARCHY_DEPTH ? [] : childNodes.map((child, i) => toReportRow(child, id, i, depth + 1, context)),
	};
}

/** Count rows that actually matched the filter, across the tree. Excludes context ancestors
 * (`matchedFilter === false`) and deliverable-tier rows: a `deliverable` is a structural container
 * promoted from the tasks beneath it (it never carries `contextOnly`, so it must be excluded
 * explicitly), not a filter-matched data row — counting it would inflate the total and the cap. */
function countMatched(rows: ReportRow[]): number {
	let n = 0;
	for (const row of rows) {
		if (row.matchedFilter && row.type !== 'deliverable') {
			n += 1;
		}
		n += countMatched(row.children);
	}
	return n;
}

/** Count every row the response returned, across the tree — matched rows, context ancestors and
 * deliverable tiers alike. The fetch `limit` bounds total rows serialized (not matched-only), so
 * this is the count the cap must be measured against. */
function countAll(rows: ReportRow[]): number {
	let n = 0;
	for (const row of rows) {
		n += 1;
		n += countAll(row.children);
	}
	return n;
}

/** The trailing metadata node the hierarchical fetch appends; absent for mock/legacy responses. */
function readMeta(nodes: FieldNode[]): {totalCount: number; cappedTotalEstimate: number; totalIsFloor: boolean} | null {
	const metaNode = nodes.find(n => n.name === 'meta');
	if (!metaNode || !isFieldNodeArray(metaNode.value)) {
		return null;
	}
	const total = field(metaNode.value, 'totalCount');
	const capped = field(metaNode.value, 'cappedTotalEstimate');
	const floor = field(metaNode.value, 'totalIsFloor');
	if (typeof total !== 'number' || typeof capped !== 'number') {
		return null;
	}
	return {totalCount: total, cappedTotalEstimate: capped, totalIsFloor: floor === true};
}

/**
 * Map the bare-array fetch response into a `ReportDataResult`.
 *
 * When the response carries a metadata node it is the source of truth for the counts and is excluded
 * from the rows. Without it (mock/offline), counts are derived client-side: `totalCount` is the
 * matched rows loaded and `capped` is whether the whole response reached the fetch limit.
 *
 * `capped` must also be true when `totalIsFloor` is set: a floored total means the fetch was
 * truncated at its page limit, so `totalCount` is a lower bound that can equal
 * `cappedTotalEstimate` while rows were still trimmed. Comparing the two counts alone would report
 * a complete result and hide the truncation.
 */
export function mapFetchResponse(
	nodes: FieldNode[],
	limit: number,
	context: RowMappingContext = DEFAULT_CONTEXT
): ReportDataResult {
	const meta = readMeta(nodes);
	const rowNodes = nodes.filter(n => n.name !== 'meta');
	const rows = rowNodes.map((node, i) => toReportRow(node, null, i, 1, context));
	if (meta) {
		return {
			rows,
			totalCount: meta.totalCount,
			cappedTotalEstimate: meta.cappedTotalEstimate,
			totalIsFloor: meta.totalIsFloor,
			capped: meta.cappedTotalEstimate < meta.totalCount || meta.totalIsFloor,
		};
	}
	const totalCount = countMatched(rows);
	return {
		rows,
		totalCount,
		cappedTotalEstimate: totalCount,
		totalIsFloor: false,
		capped: countAll(rows) >= limit,
	};
}
